export type TAlignedWordPair<TPrimary, TCompare> = {
  primary: TPrimary | null;
  compare: TCompare | null;
};

type TWordPairScorer<TPrimary, TCompare> = (primary: TPrimary, compare: TCompare) => number;

const DIAGONAL = 1;
const UP = 2;
const LEFT = 3;
const DEFAULT_ALIGNMENT_BAND_WIDTH = 256;
const DEFAULT_MAX_POINTER_CELLS = 32 * 1024 * 1024;
const DEFAULT_FALLBACK_CHUNK_SIZE = 2048;
const UNREACHABLE_SCORE = Number.NEGATIVE_INFINITY;

export type TAlignmentLimits = {
  maxPointerCells?: number;
  fallbackChunkSize?: number;
};

type TAlignmentAttempt<TPrimary, TCompare> = {
  alignment: Array<TAlignedWordPair<TPrimary, TCompare>>;
  reachedEnd: boolean;
  touchedBoundary: boolean;
};

const alignWithinBand = <TPrimary extends { text: string }, TCompare extends { text: string }>(
  primaryWords: TPrimary[],
  compareWords: TCompare[],
  bandWidth: number,
  matchScore = 1,
  mismatchPenalty = -1,
  gapPenalty = -1,
  scoreWordPair?: TWordPairScorer<TPrimary, TCompare>,
): TAlignmentAttempt<TPrimary, TCompare> => {
  const primaryLength = primaryWords.length;
  const compareLength = compareWords.length;
  const rowStarts = new Int32Array(primaryLength + 1);
  const rowEnds = new Int32Array(primaryLength + 1);
  const pointers: Uint8Array[] = new Array(primaryLength + 1);
  let previousStart = 0;
  const firstRowEnd = Math.min(compareLength, bandWidth);
  let previousScores = new Float64Array(firstRowEnd + 1);
  const firstRowPointers = new Uint8Array(firstRowEnd + 1);

  rowEnds[0] = firstRowEnd;
  for (let compareIndex = 1; compareIndex <= firstRowEnd; compareIndex++) {
    previousScores[compareIndex] = gapPenalty * compareIndex;
    firstRowPointers[compareIndex] = LEFT;
  }
  pointers[0] = firstRowPointers;

  for (let primaryIndex = 1; primaryIndex <= primaryLength; primaryIndex++) {
    const center = Math.round((primaryIndex * compareLength) / primaryLength);
    const currentStart = Math.max(0, center - bandWidth);
    const currentEnd = Math.min(compareLength, center + bandWidth);
    const currentScores = new Float64Array(currentEnd - currentStart + 1);
    const currentPointers = new Uint8Array(currentEnd - currentStart + 1);

    currentScores.fill(UNREACHABLE_SCORE);
    rowStarts[primaryIndex] = currentStart;
    rowEnds[primaryIndex] = currentEnd;

    for (let compareIndex = currentStart; compareIndex <= currentEnd; compareIndex++) {
      const cellIndex = compareIndex - currentStart;

      if (compareIndex === 0) {
        currentScores[cellIndex] = gapPenalty * primaryIndex;
        currentPointers[cellIndex] = UP;
        continue;
      }

      const previousDiagonalIndex = compareIndex - 1 - previousStart;
      const previousUpIndex = compareIndex - previousStart;
      const diagonalScore =
        previousDiagonalIndex >= 0 && previousDiagonalIndex < previousScores.length
          ? previousScores[previousDiagonalIndex] +
            (scoreWordPair
              ? scoreWordPair(primaryWords[primaryIndex - 1], compareWords[compareIndex - 1])
              : primaryWords[primaryIndex - 1].text.toLowerCase() === compareWords[compareIndex - 1].text.toLowerCase()
                ? matchScore
                : mismatchPenalty)
          : UNREACHABLE_SCORE;
      const upScore =
        previousUpIndex >= 0 && previousUpIndex < previousScores.length
          ? previousScores[previousUpIndex] + gapPenalty
          : UNREACHABLE_SCORE;
      const leftScore = cellIndex > 0 ? currentScores[cellIndex - 1] + gapPenalty : UNREACHABLE_SCORE;
      const bestScore = Math.max(diagonalScore, upScore, leftScore);

      currentScores[cellIndex] = bestScore;
      currentPointers[cellIndex] = bestScore === diagonalScore ? DIAGONAL : bestScore === upScore ? UP : LEFT;
    }

    pointers[primaryIndex] = currentPointers;
    previousStart = currentStart;
    previousScores = currentScores;
  }

  const endScoreIndex = compareLength - previousStart;
  const reachedEnd =
    endScoreIndex >= 0 && endScoreIndex < previousScores.length && Number.isFinite(previousScores[endScoreIndex]);

  if (!reachedEnd) {
    return { alignment: [], reachedEnd: false, touchedBoundary: true };
  }

  const alignment: Array<TAlignedWordPair<TPrimary, TCompare>> = [];
  let primaryIndex = primaryLength;
  let compareIndex = compareLength;
  let touchedBoundary = false;

  while (primaryIndex > 0 || compareIndex > 0) {
    if (
      (compareIndex === rowStarts[primaryIndex] && compareIndex > 0) ||
      (compareIndex === rowEnds[primaryIndex] && compareIndex < compareLength)
    ) {
      touchedBoundary = true;
    }

    const pointerIndex = compareIndex - rowStarts[primaryIndex];
    const pointer = pointers[primaryIndex]?.[pointerIndex];

    if (pointer === DIAGONAL) {
      alignment.push({
        primary: primaryWords[primaryIndex - 1],
        compare: compareWords[compareIndex - 1],
      });
      primaryIndex--;
      compareIndex--;
      continue;
    }

    if (pointer === UP) {
      alignment.push({ primary: primaryWords[primaryIndex - 1], compare: null });
      primaryIndex--;
      continue;
    }

    if (pointer === LEFT) {
      alignment.push({ primary: null, compare: compareWords[compareIndex - 1] });
      compareIndex--;
      continue;
    }

    return { alignment: [], reachedEnd: false, touchedBoundary: true };
  }

  alignment.reverse();
  return { alignment, reachedEnd: true, touchedBoundary };
};

const getAffordableBandWidth = (primaryLength: number, maxPointerCells: number): number =>
  Math.floor((maxPointerCells / (primaryLength + 1) - 1) / 2);

type TAnchor = {
  primaryIndex: number;
  compareIndex: number;
};

const ANCHOR_NGRAM_SIZE = 3;

const buildUniqueNgramStarts = <TWord extends { text: string }>(words: TWord[]): Map<string, number> => {
  const starts = new Map<string, number>();
  const duplicates = new Set<string>();

  for (let index = 0; index + ANCHOR_NGRAM_SIZE <= words.length; index++) {
    let key = words[index].text.toLowerCase();

    for (let offset = 1; offset < ANCHOR_NGRAM_SIZE; offset++) {
      key += '\u0000' + words[index + offset].text.toLowerCase();
    }

    if (duplicates.has(key)) {
      continue;
    }

    if (starts.has(key)) {
      starts.delete(key);
      duplicates.add(key);
      continue;
    }

    starts.set(key, index);
  }

  return starts;
};

const collectAnchorCandidates = <TPrimary extends { text: string }, TCompare extends { text: string }>(
  primaryWords: TPrimary[],
  compareWords: TCompare[],
): TAnchor[] => {
  const primaryStarts = buildUniqueNgramStarts(primaryWords);
  const compareStarts = buildUniqueNgramStarts(compareWords);
  const candidates: TAnchor[] = [];

  primaryStarts.forEach((primaryIndex, key) => {
    const compareIndex = compareStarts.get(key);

    if (typeof compareIndex === 'number') {
      candidates.push({ primaryIndex, compareIndex });
    }
  });

  return candidates.sort((left, right) => left.primaryIndex - right.primaryIndex);
};

const findMonotoneAnchorChain = (candidates: TAnchor[]): TAnchor[] => {
  const tailCandidateIndexes: number[] = [];
  const previousCandidateIndexes = new Int32Array(candidates.length).fill(-1);

  candidates.forEach((candidate, candidateIndex) => {
    let low = 0;
    let high = tailCandidateIndexes.length;

    while (low < high) {
      const middle = (low + high) >> 1;

      if (candidates[tailCandidateIndexes[middle]].compareIndex < candidate.compareIndex) {
        low = middle + 1;
      } else {
        high = middle;
      }
    }

    if (low > 0) {
      previousCandidateIndexes[candidateIndex] = tailCandidateIndexes[low - 1];
    }

    tailCandidateIndexes[low] = candidateIndex;
  });

  const chain: TAnchor[] = [];
  let cursor = tailCandidateIndexes.length > 0 ? tailCandidateIndexes[tailCandidateIndexes.length - 1] : -1;

  while (cursor >= 0) {
    chain.push(candidates[cursor]);
    cursor = previousCandidateIndexes[cursor];
  }

  return chain.reverse();
};

const selectAnchorCuts = (
  anchors: TAnchor[],
  targetSegmentSize: number,
  primaryLength: number,
  compareLength: number,
): TAnchor[] => {
  const usefulAnchors = anchors.filter(
    (anchor) =>
      (anchor.primaryIndex > 0 || anchor.compareIndex > 0) &&
      (anchor.primaryIndex < primaryLength || anchor.compareIndex < compareLength),
  );
  const cuts: TAnchor[] = [];
  let previousCut: TAnchor = { primaryIndex: 0, compareIndex: 0 };

  usefulAnchors.forEach((anchor, anchorIndex) => {
    const progress = Math.max(
      anchor.primaryIndex - previousCut.primaryIndex,
      anchor.compareIndex - previousCut.compareIndex,
    );

    if (progress >= targetSegmentSize || anchorIndex === usefulAnchors.length - 1) {
      cuts.push(anchor);
      previousCut = anchor;
    }
  });

  return cuts;
};

const alignProportionalChunks = <TPrimary extends { text: string }, TCompare extends { text: string }>(
  primaryWords: TPrimary[],
  compareWords: TCompare[],
  matchScore: number,
  mismatchPenalty: number,
  gapPenalty: number,
  scoreWordPair: TWordPairScorer<TPrimary, TCompare> | undefined,
  limits: TAlignmentLimits | undefined,
): Array<TAlignedWordPair<TPrimary, TCompare>> => {
  const fallbackChunkSize = limits?.fallbackChunkSize ?? DEFAULT_FALLBACK_CHUNK_SIZE;
  const chunkCount = Math.max(1, Math.ceil(Math.max(primaryWords.length, compareWords.length) / fallbackChunkSize));
  const alignment: Array<TAlignedWordPair<TPrimary, TCompare>> = [];

  for (let chunkIndex = 0; chunkIndex < chunkCount; chunkIndex++) {
    const primaryStart = Math.round((chunkIndex * primaryWords.length) / chunkCount);
    const primaryEnd = Math.round(((chunkIndex + 1) * primaryWords.length) / chunkCount);
    const compareStart = Math.round((chunkIndex * compareWords.length) / chunkCount);
    const compareEnd = Math.round(((chunkIndex + 1) * compareWords.length) / chunkCount);
    const chunkAlignment = alignTranscriptWords(
      primaryWords.slice(primaryStart, primaryEnd),
      compareWords.slice(compareStart, compareEnd),
      matchScore,
      mismatchPenalty,
      gapPenalty,
      scoreWordPair,
      limits,
    );

    alignment.push(...chunkAlignment);
  }

  return alignment;
};

const alignInChunks = <TPrimary extends { text: string }, TCompare extends { text: string }>(
  primaryWords: TPrimary[],
  compareWords: TCompare[],
  matchScore: number,
  mismatchPenalty: number,
  gapPenalty: number,
  scoreWordPair: TWordPairScorer<TPrimary, TCompare> | undefined,
  limits: TAlignmentLimits | undefined,
): Array<TAlignedWordPair<TPrimary, TCompare>> => {
  const fallbackChunkSize = limits?.fallbackChunkSize ?? DEFAULT_FALLBACK_CHUNK_SIZE;
  const anchorCuts = selectAnchorCuts(
    findMonotoneAnchorChain(collectAnchorCandidates(primaryWords, compareWords)),
    fallbackChunkSize,
    primaryWords.length,
    compareWords.length,
  );

  if (anchorCuts.length === 0) {
    return alignProportionalChunks(
      primaryWords,
      compareWords,
      matchScore,
      mismatchPenalty,
      gapPenalty,
      scoreWordPair,
      limits,
    );
  }

  const cutPoints: TAnchor[] = [
    { primaryIndex: 0, compareIndex: 0 },
    ...anchorCuts,
    { primaryIndex: primaryWords.length, compareIndex: compareWords.length },
  ];
  const alignment: Array<TAlignedWordPair<TPrimary, TCompare>> = [];

  for (let cutIndex = 0; cutIndex + 1 < cutPoints.length; cutIndex++) {
    alignment.push(
      ...alignTranscriptWords(
        primaryWords.slice(cutPoints[cutIndex].primaryIndex, cutPoints[cutIndex + 1].primaryIndex),
        compareWords.slice(cutPoints[cutIndex].compareIndex, cutPoints[cutIndex + 1].compareIndex),
        matchScore,
        mismatchPenalty,
        gapPenalty,
        scoreWordPair,
        limits,
      ),
    );
  }

  return alignment;
};

export const alignTranscriptWords = <TPrimary extends { text: string }, TCompare extends { text: string }>(
  primaryWords: TPrimary[],
  compareWords: TCompare[],
  matchScore = 1,
  mismatchPenalty = -1,
  gapPenalty = -1,
  scoreWordPair?: TWordPairScorer<TPrimary, TCompare>,
  limits?: TAlignmentLimits,
): Array<TAlignedWordPair<TPrimary, TCompare>> => {
  if (primaryWords.length === 0) {
    return compareWords.map((compare) => ({ primary: null, compare }));
  }

  if (compareWords.length === 0) {
    return primaryWords.map((primary) => ({ primary, compare: null }));
  }

  const maxPointerCells = limits?.maxPointerCells ?? DEFAULT_MAX_POINTER_CELLS;
  const fallbackChunkSize = limits?.fallbackChunkSize ?? DEFAULT_FALLBACK_CHUNK_SIZE;
  const maximumBandWidth = Math.max(primaryWords.length, compareWords.length);
  const affordableBandWidth = Math.max(1, getAffordableBandWidth(primaryWords.length, maxPointerCells));
  const bandWidthCap =
    maximumBandWidth > fallbackChunkSize ? Math.min(affordableBandWidth, maximumBandWidth) : maximumBandWidth;
  let bandWidth = Math.min(DEFAULT_ALIGNMENT_BAND_WIDTH, bandWidthCap);

  while (true) {
    const attempt = alignWithinBand(
      primaryWords,
      compareWords,
      bandWidth,
      matchScore,
      mismatchPenalty,
      gapPenalty,
      scoreWordPair,
    );

    if (attempt.reachedEnd && (!attempt.touchedBoundary || bandWidth === maximumBandWidth)) {
      return attempt.alignment;
    }

    if (bandWidth >= bandWidthCap) {
      return alignInChunks(primaryWords, compareWords, matchScore, mismatchPenalty, gapPenalty, scoreWordPair, limits);
    }

    bandWidth = Math.min(bandWidth * 2, bandWidthCap);
  }
};
